/**
 * <back-to-top>
 * The scroll-to-top control in the pagination row. It pins to the bottom-left
 * of the viewport while the row is still below the fold, and drops back into
 * the row once the row is reached.
 */
if (!customElements.get('back-to-top')) {
  class BackToTop extends HTMLElement {
    // Hand the control over to the row a little before the row is on screen,
    // so the swap happens off the bottom edge rather than in plain sight.
    static ROW_MARGIN = '0px 0px 80px 0px';

    #controller = null;
    #observer = null;
    #rowBelow = false;
    #scrolledAway = false;

    connectedCallback() {
      const button = this.querySelector('button');
      if (!button) return;

      // One AbortController detaches every listener on disconnect.
      const { signal } = (this.#controller = new AbortController());
      button.addEventListener('click', () => BackToTop.scrollToTop(), { signal });

      this.#watchRow();
      window.addEventListener('scroll', () => this.#handleScroll(), { passive: true, signal });
      this.#handleScroll();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#controller = null;
      this.#observer = null;
    }

    /**
     * Float while the row sits below the viewport. `isIntersecting` alone does
     * not say which side it is off, and above the viewport the control must
     * stay put — the reader has already scrolled past it.
     */
    #watchRow() {
      const row = this.closest('.pagination-wrapper');
      if (!row) return;

      this.#observer = new IntersectionObserver(
        ([entry]) => {
          this.#rowBelow = !entry.isIntersecting && entry.boundingClientRect.top > 0;
          this.#update();
        },
        { root: null, rootMargin: BackToTop.ROW_MARGIN, threshold: 0 },
      );
      this.#observer.observe(row);
    }

    // A scroll-to-top control has nothing to offer at the top of the page.
    #handleScroll() {
      const away = window.scrollY > window.innerHeight;
      if (away === this.#scrolledAway) return;
      this.#scrolledAway = away;
      this.#update();
    }

    #update() {
      this.classList.toggle('is-floating', this.#rowBelow && this.#scrolledAway);
    }

    static scrollToTop() {
      // CSS scroll-behavior does not reach a programmatic window scroll, so the
      // reduced-motion gate has to be read here.
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  customElements.define('back-to-top', BackToTop);
}
